// src/pages/Admin/DossierInscription/components/DossiersTable.jsx

import React from "react";
import {
  FaEdit,
  FaExternalLinkAlt,
  FaFolderOpen,
  FaFolderPlus,
  FaGoogleDrive,
  FaServer,
  FaTrash,
} from "react-icons/fa";

import {
  formatDateFr,
  getAdherentId,
  getAdherentName,
  getBoolDisplay,
  getDossierCompletion,
  getDossierId,
  getGroupsText,
  isDriveUrl,
  isServerFile,
  toAbsoluteUrl,
} from "../dossier.helpers";

import ButtonMail from "./../../../../../components/Button/ButtonMail";

function sortIcon(order) {
  return order === "asc" ? "▲" : "▼";
}

function FileCell({ url, title }) {
  if (!url) {
    return <span className="text-slate-400">—</span>;
  }

  const absoluteUrl = toAbsoluteUrl(url);
  const drive = isDriveUrl(url);
  const server = isServerFile(url);

  return (
    <a
      href={absoluteUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      title={title}
    >
      {drive ? (
        <FaGoogleDrive className="text-emerald-600 dark:text-emerald-300" />
      ) : server ? (
        <FaServer className="text-indigo-600 dark:text-indigo-300" />
      ) : (
        <FaExternalLinkAlt />
      )}
    </a>
  );
}

function BoolButton({ value, onClick, disabled, label }) {
  const display = getBoolDisplay(value);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex min-w-[74px] justify-center rounded-full px-3 py-1 text-xs font-bold ring-1 transition disabled:cursor-not-allowed disabled:opacity-60 ${display.className}`}
      title={`Basculer ${label}`}
    >
      {display.label}
    </button>
  );
}

export default function DossiersTable({
  rows,
  totalCount,
  visibleCount,
  loading,
  scrollRef,
  onScroll,
  fsFlags,
  folderCreating,
  isRowRefreshing,
  toggleValidate,
  handleEnsureFolder,
  sortField,
  sortOrder,
  toggleNameSort,
  toggleCompletionSort,
  onEdit,
  onDelete,
  onSendReminder,
}) {
  return (
    <div className="p-3 sm:p-5 lg:p-6">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="max-h-[72vh] overflow-auto rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/60"
      >
        <table className="min-w-[1480px] w-full text-sm">
          <thead className="sticky top-0 z-30 bg-slate-50 dark:bg-slate-900">
            <tr className="text-left">
              <th className="sticky left-0 z-40 w-[280px] bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                <button
                  type="button"
                  onClick={toggleNameSort}
                  className="inline-flex items-center gap-2 rounded-xl px-2 py-1 transition hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Adhérent
                  <span>
                    {sortField === "name" ? sortIcon(sortOrder) : "⇅"}
                  </span>
                </button>
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                <button
                  type="button"
                  onClick={toggleCompletionSort}
                  className="inline-flex items-center gap-2 rounded-xl px-2 py-1 transition hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Avancement
                  <span>
                    {sortField === "completion" ? sortIcon(sortOrder) : "⇅"}
                  </span>
                </button>
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Dossier serveur
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                CM
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Date CM
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Valid. CM
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Form. à signer
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Form. signé
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Licence
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Valid. LIC
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Valid. REG
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Drive
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Groupes
              </th>

              <th className="sticky right-0 z-40 w-[120px] bg-slate-50 px-4 py-3 text-right font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {loading ? (
              [...Array(8)].map((_, index) => (
                <tr key={index}>
                  <td colSpan={14} className="px-4 py-3">
                    <div className="h-10 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
                  </td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={14}
                  className="px-4 py-10 text-center text-slate-500 dark:text-slate-400"
                >
                  Aucun dossier ne correspond aux filtres.
                </td>
              </tr>
            ) : (
              rows.map((dossier) => {
                const id = getDossierId(dossier);
                const adherentId = getAdherentId(dossier);
                const completion = getDossierCompletion(dossier);
                const fsOk = fsFlags?.[String(adherentId)];
                const refreshing = isRowRefreshing?.(id);

                return (
                  <tr
                    key={id}
                    className="transition hover:bg-slate-50 dark:hover:bg-slate-900"
                  >
                    <td className="sticky left-0 z-20 bg-white px-4 py-3 dark:bg-slate-950">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {getAdherentName(dossier)}
                      </div>

                      {/* <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        ID dossier {id} · ID adhérent {adherentId || "—"}
                      </div> */}
                    </td>

                    <td className="px-4 py-3">
                      <div className="w-[130px]">
                        <div className="mb-1 flex items-center justify-between text-xs font-bold text-slate-600 dark:text-slate-300">
                          <span>
                            {completion.done}/{completion.total}
                          </span>
                          <span>{completion.percent}%</span>
                        </div>

                        <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                          <div
                            className={`h-full rounded-full ${
                              completion.complete
                                ? "bg-emerald-500"
                                : "bg-indigo-500"
                            }`}
                            style={{ width: `${completion.percent}%` }}
                          />
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      {fsOk ? (
                        <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                          <FaFolderOpen />
                          OK
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleEnsureFolder(adherentId)}
                          disabled={!adherentId || folderCreating?.[adherentId]}
                          className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 transition hover:bg-indigo-100 disabled:opacity-60 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20 dark:hover:bg-indigo-500/20"
                        >
                          <FaFolderPlus />
                          {folderCreating?.[adherentId] ? "..." : "Créer"}
                        </button>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <FileCell
                        url={dossier?.cm_url}
                        title="Certificat médical"
                      />
                    </td>

                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                      {formatDateFr(dossier?.date_cm)}
                    </td>

                    <td className="px-4 py-3">
                      <BoolButton
                        value={dossier?.validate_cm}
                        disabled={refreshing}
                        label="CM"
                        onClick={() =>
                          toggleValidate(dossier, "validate_cm", "CM")
                        }
                      />
                    </td>

                    <td className="px-4 py-3">
                      <FileCell
                        url={dossier?.formulaire_licence_a_signer}
                        title="Formulaire à signer"
                      />
                    </td>

                    <td className="px-4 py-3">
                      <FileCell
                        url={
                          dossier?.formulaire_licence_signe ||
                          dossier?.formulaire_licence_signee
                        }
                        title="Formulaire signé"
                      />
                    </td>

                    <td className="px-4 py-3">
                      <FileCell
                        url={
                          dossier?.licence_url || dossier?.licence_assurance_url
                        }
                        title="Licence"
                      />
                    </td>

                    <td className="px-4 py-3">
                      <BoolButton
                        value={dossier?.validate_licence}
                        disabled={refreshing}
                        label="Licence"
                        onClick={() =>
                          toggleValidate(dossier, "validate_licence", "Licence")
                        }
                      />
                    </td>

                    <td className="px-4 py-3">
                      <BoolButton
                        value={dossier?.validate_reglement}
                        disabled={refreshing}
                        label="Règlement"
                        onClick={() =>
                          toggleValidate(
                            dossier,
                            "validate_reglement",
                            "Règlement",
                          )
                        }
                      />
                    </td>

                    <td className="px-4 py-3">
                      <FileCell
                        url={dossier?.lien_drive}
                        title="Dossier Drive"
                      />
                    </td>

                    <td className="px-4 py-3">
                      <div className="max-w-[260px] truncate text-slate-700 dark:text-slate-300">
                        {getGroupsText(dossier) || "—"}
                      </div>
                    </td>

                    <td className="sticky right-0 z-20 bg-white px-4 py-3 dark:bg-slate-950">
                      <div className="flex justify-end gap-2">
                        {!completion.complete && (
                          <ButtonMail
                            onClick={() => {
                              onSendReminder?.(dossier);
                            }}
                            hoverLabel="Envoyer un rappel"
                          />
                        )}

                        <button
                          type="button"
                          onClick={() => onEdit(dossier)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                          title="Modifier"
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          onClick={() => onDelete(id)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white transition hover:bg-rose-700"
                          title="Supprimer"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {!loading && visibleCount < totalCount ? (
          <div className="border-t border-slate-200 p-3 text-center text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
            Affichage de {visibleCount} / {totalCount} — descendez pour charger
            la suite.
          </div>
        ) : null}
      </div>
    </div>
  );
}
